// APP USAGE — which apps you actually reach for.
//
// Every launch stamps the app id, so the desktop and the store can put the
// things you use first. Counts and times never leave this device.

const KEY = "sophia:apps:usage";

export interface UsageRow {
  id: string;
  /** last opened, ms epoch */
  at: number;
  /** how many times it has been opened on this device */
  count: number;
}

type Map_ = Record<string, UsageRow>;

function read(): Map_ {
  try {
    const raw = typeof localStorage === "undefined" ? null : localStorage.getItem(KEY);
    const parsed = raw ? (JSON.parse(raw) as Map_) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function write(map: Map_): Map_ {
  try {
    localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* private mode */
  }
  try {
    window.dispatchEvent(new CustomEvent("sophia:apps:usage"));
  } catch {
    /* SSR */
  }
  return map;
}

export function usage(): Map_ {
  return read();
}

export function usageFor(id: string): UsageRow | undefined {
  return read()[id];
}

/** Call the moment an app is opened. */
export function markUsed(id: string): void {
  const map = read();
  const row = map[id];
  map[id] = { id, at: Date.now(), count: (row?.count ?? 0) + 1 };
  write(map);
}

/** Ids, most recently used first. */
export function recentIds(limit = 8): string[] {
  return Object.values(read())
    .sort((a, b) => b.at - a.at)
    .slice(0, limit)
    .map((r) => r.id);
}

/** Sorts anything with an id: used apps first (newest first), then the rest. */
export function byLastUsed<T extends { id: string }>(rows: readonly T[]): T[] {
  const map = read();
  return [...rows].sort((a, b) => (map[b.id]?.at ?? 0) - (map[a.id]?.at ?? 0));
}

export function clearUsage(): void {
  write({});
}

export function subscribeUsage(fn: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const h = () => fn();
  window.addEventListener("sophia:apps:usage", h);
  window.addEventListener("storage", h);
  return () => {
    window.removeEventListener("sophia:apps:usage", h);
    window.removeEventListener("storage", h);
  };
}

export function agoLabel(at: number | undefined): string {
  if (!at) return "not opened yet";
  const s = Math.max(1, Math.round((Date.now() - at) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}
